import { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import {
	type InterviewProcessingState,
	parseInterviewProcessingState,
} from '@Domain/Enums/Interviews/InterviewProcessingState';
import type { OutboxItemState } from '@Domain/Ports/Outbox/OutboxPort';
import type PlainDate from '@Domain/PlainDate';
import type InternalInterviewParticipantView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewParticipantView';
import type { LocalInterviewCaptureState } from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/localInterviewCaptureState';
import type LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';

const DETAIL_ARIA_LABEL_WITHOUT_DATE: Readonly<Record<InterviewKind, string>> = Object.freeze({
	[InterviewKind.CONVERSATION]: "Détail de l'entretien (date inconnue)",
	[InterviewKind.NOTES]: 'Détail de la prise de notes (date inconnue)',
	[InterviewKind.TEXT]: 'Détail du texte saisi (date inconnue)',
});

const DETAIL_ARIA_LABEL_WITH_DATE: Readonly<Record<InterviewKind, (date: string) => string>> = Object.freeze({
	[InterviewKind.CONVERSATION]: (date) => `Détail de l'entretien du ${date}`,
	[InterviewKind.NOTES]: (date) => `Détail de la prise de notes du ${date}`,
	[InterviewKind.TEXT]: (date) => `Détail du texte saisi du ${date}`,
});

/** The resolved facts one row is built from — never an API payload, unlike a store View. */
type Args = {
	readonly key: string;
	readonly origin: 'server' | 'local';
	readonly interviewId: number | null;
	readonly clientReference: string | null;
	readonly localInterviewId: string | null;
	readonly outboxItemId: string | null;
	readonly conductedOn: PlainDate | null;
	readonly kind: string | null;
	readonly name: string | null;
	readonly participants: readonly InternalInterviewParticipantView[];
	readonly transferState: 'processing' | 'sent' | null;
	readonly captureState: LocalInterviewCaptureState | null;
	readonly outboxState: OutboxItemState | null;
	readonly isConsentWithdrawn: boolean;
	readonly consentId: number | null;
	readonly processingState: LabeledValueView | null;
};

/**
 * Immutable View of one row of the folder's interview list, whichever side it comes from: `readonly`
 * properties, frozen at the end of the constructor, no setter — `mergeInterviewEntries` rebuilds the
 * whole list from its three sources rather than mutating a row.
 *
 * A row born on this device and later confirmed by the server is never two rows: it is fused into one,
 * which is why `localInterviewId`/`outboxItemId`/`outboxState` are carried even on a `'server'` origin.
 * The accessors below answer the questions the list screen asks of a row, so that no screen spells out
 * a two- or three-term condition over these nullable fields for itself.
 */
class InternalInterviewListEntryView {
	/** Stable React key: the server id when it exists, otherwise the local UUID. */
	readonly key: string;
	readonly origin: 'server' | 'local';
	readonly interviewId: number | null;
	readonly clientReference: string | null;
	/** Carried even on a merged server entry: it is what the retry/discard actions need. */
	readonly localInterviewId: string | null;
	/** Carried even on a merged server entry: without it the queue actions have no target. */
	readonly outboxItemId: string | null;
	readonly conductedOn: PlainDate | null;
	/** The enum value; `null` on a local interview predating the field. The label always comes from the server, never from here. */
	readonly kind: string | null;
	/** `null` on a row predating the required name (A9), or a local row carrying none yet. */
	readonly name: string | null;
	readonly participants: readonly InternalInterviewParticipantView[];
	/**
	 * `null` on a server entry whose `recording` is itself `null` (a row carrying none, or a withdrawn
	 * consent that deleted it): the transfer axis has two points, and such a row is on neither. Never
	 * invent a third badge for it.
	 */
	readonly transferState: 'processing' | 'sent' | null;
	/**
	 * Whether a capture may still be started for this interview. Reuses the predicate's own return
	 * type — never a second union spelling — and is `null` on a server-only entry.
	 */
	readonly captureState: LocalInterviewCaptureState | null;
	/** The queue state, rendered with `outboxItemStateLabel`; `null` when no queue item is known. */
	readonly outboxState: OutboxItemState | null;
	readonly isConsentWithdrawn: boolean;
	readonly consentId: number | null;
	/** The server's reading of where the interview stands against its treatment; `null` on a purely local row. */
	readonly processingState: LabeledValueView | null;

	constructor(parts: Args) {
		this.key = parts.key;
		this.origin = parts.origin;
		this.interviewId = parts.interviewId;
		this.clientReference = parts.clientReference;
		this.localInterviewId = parts.localInterviewId;
		this.outboxItemId = parts.outboxItemId;
		this.conductedOn = parts.conductedOn;
		this.kind = parts.kind;
		this.name = parts.name;
		this.participants = parts.participants;
		this.transferState = parts.transferState;
		this.captureState = parts.captureState;
		this.outboxState = parts.outboxState;
		this.isConsentWithdrawn = parts.isConsentWithdrawn;
		this.consentId = parts.consentId;
		this.processingState = parts.processingState;

		Object.freeze(this);
	}

	/** The row's kind, a conversation when the value is absent or unknown to this client. */
	private get resolvedKind(): InterviewKind {
		return Object.values(InterviewKind).find((kind) => kind === this.kind) ?? InterviewKind.CONVERSATION;
	}

	/** The row exists on this device only: no server interview has confirmed it yet. */
	get isLocal(): boolean {
		return this.origin === 'local';
	}

	/** The row is a note-taking (no conversation recorded), as opposed to an interview. */
	get isNotes(): boolean {
		return this.kind === InterviewKind.NOTES;
	}

	/** The row is an entered text, as opposed to an interview or a note-taking. */
	get isTextEntry(): boolean {
		return this.kind === InterviewKind.TEXT;
	}

	get isProcessing(): boolean {
		return this.transferState === 'processing';
	}

	get hasServerInterview(): boolean {
		return this.interviewId !== null;
	}

	/**
	 * The interview to resume a capture on, or `null` when this row offers none — a capture may be
	 * resumed only where one was started on this very device. Returns the id rather than a boolean so
	 * the caller reads a narrowed value instead of re-testing the same field the predicate just tested.
	 */
	get resumableCaptureLocalInterviewId(): string | null {
		if (!this.isLocal || this.captureState !== 'capturable') {
			return null;
		}

		return this.localInterviewId;
	}

	/** Retry and discard both need a queue item AND the local row it belongs to; neither alone. */
	get queueActionTarget(): { readonly outboxItemId: string; readonly localInterviewId: string } | null {
		if (this.outboxItemId === null || this.localInterviewId === null) {
			return null;
		}

		return { outboxItemId: this.outboxItemId, localInterviewId: this.localInterviewId };
	}

	/** The consent screen reads a server row: an id without an interview to hang it on is unreachable. */
	get hasConsentLink(): boolean {
		return this.consentId !== null && this.interviewId !== null;
	}

	/** `null` when the server sent a state this front does not know, or on a purely local row. */
	get processingStateKey(): InterviewProcessingState | null {
		return this.processingState === null
			? null
			: parseInterviewProcessingState(this.processingState.value);
	}

	get participantsDisplay(): string {
		if (this.participants.length === 0) {
			return 'Participants non renseignés';
		}

		return this.participants.map((participant) => participant.label).join(', ');
	}

	/** A row whose date is unknown still renders one: the list never shows an empty date cell. */
	get conductedOnDisplay(): string {
		return this.conductedOn?.toDisplay() ?? 'Date inconnue';
	}

	/** Whether `title` renders the name rather than falling back to the date (A9). */
	get hasName(): boolean {
		return (this.name?.trim() ?? '') !== '';
	}

	/** The row's title: the name when the interview carries one, the date otherwise (A9). */
	get title(): string {
		const trimmedName = this.name?.trim() ?? '';

		return trimmedName !== '' ? trimmedName : this.conductedOnDisplay;
	}

	/**
	 * The date alone is not a name: several rows may share it, and a row without one would offer a
	 * link with no accessible name at all.
	 */
	get detailAriaLabel(): string {
		if (this.conductedOn === null) {
			return DETAIL_ARIA_LABEL_WITHOUT_DATE[this.resolvedKind];
		}

		return DETAIL_ARIA_LABEL_WITH_DATE[this.resolvedKind](this.conductedOn.toDisplay());
	}
}

export default InternalInterviewListEntryView;
